import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, getDoc, collection, query, onSnapshot, orderBy, limit, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { firestore } from '../FirebaseConfig';
import { useAuth } from './CombinedAuthContext';

const AdminContext = createContext(null);

const functions = getFunctions(undefined, 'us-central1');

/**
 * Admin role definitions
 */
export const ADMIN_ROLES = {
  SUPER_ADMIN: 'super_admin',
  SUPER_USER: 'super_user', // Alias for super_admin
  CONTENT_ADMIN: 'content_admin',
  SUPPORT_ADMIN: 'support_admin',
  ANALYTICS_ADMIN: 'analytics_admin',
  MODERATOR: 'moderator'
};

/**
 * Map role names to normalized roles
 */
const normalizeRole = (role) => {
  const roleMap = {
    'super_user': ADMIN_ROLES.SUPER_ADMIN,
    'superUser': ADMIN_ROLES.SUPER_ADMIN,
    'super_admin': ADMIN_ROLES.SUPER_ADMIN,
    'superAdmin': ADMIN_ROLES.SUPER_ADMIN,
    'content_admin': ADMIN_ROLES.CONTENT_ADMIN,
    'contentAdmin': ADMIN_ROLES.CONTENT_ADMIN,
    'support_admin': ADMIN_ROLES.SUPPORT_ADMIN,
    'supportAdmin': ADMIN_ROLES.SUPPORT_ADMIN,
    'analytics_admin': ADMIN_ROLES.ANALYTICS_ADMIN,
    'analyticsAdmin': ADMIN_ROLES.ANALYTICS_ADMIN,
    'moderator': ADMIN_ROLES.MODERATOR,
    'admin': ADMIN_ROLES.CONTENT_ADMIN
  };
  return roleMap[role] || ADMIN_ROLES.CONTENT_ADMIN;
};

/**
 * Permission definitions by role
 */
const ROLE_PERMISSIONS = {
  [ADMIN_ROLES.SUPER_ADMIN]: [
    'users.view', 'users.edit', 'users.delete',
    'listings.view', 'listings.edit', 'listings.delete', 'listings.approve', 'listings.moderate',
    'content.view', 'content.edit',
    'reports.view', 'reports.export',
    'royalty.view', 'royalty.export', 'royalty.manage',
    'settings.view', 'settings.edit',
    'alerts.view', 'alerts.acknowledge',
    'admins.view', 'admins.manage',
    'analytics.view',
    'all' // Super admin has all permissions
  ],
  // "Admin" — the non-super operational admin. Everything EXCEPT site/security
  // settings + token packs (settings.*) and the audit/oversight surface
  // (admins.view). Can manage LOWER admin tiers only via admins.manage
  // (support/analytics/moderator) — the per-target scope is enforced in
  // functions/adminManagementFn.js. Keep in sync with functions/adminPermissions.js.
  [ADMIN_ROLES.CONTENT_ADMIN]: [
    'users.view', 'users.edit', 'users.delete',
    'listings.view', 'listings.edit', 'listings.delete', 'listings.approve', 'listings.moderate',
    'content.view', 'content.edit',
    'reports.view', 'reports.export',
    // Royalty engine: read-only + report export (scoped). NOT royalty.manage.
    'royalty.view', 'royalty.export',
    'alerts.view', 'alerts.acknowledge',
    'analytics.view',
    'admins.manage'
  ],
  [ADMIN_ROLES.SUPPORT_ADMIN]: [
    'users.view',
    'listings.view',
    'alerts.view', 'alerts.acknowledge'
  ],
  [ADMIN_ROLES.ANALYTICS_ADMIN]: [
    'reports.view', 'reports.export',
    'analytics.view',
    'alerts.view'
  ],
  // Moderator — view + approve + moderate listings/properties (reviews,
  // reports queue, moderation queue, map editor). No user/settings/admin access.
  [ADMIN_ROLES.MODERATOR]: [
    'listings.view', 'listings.approve', 'listings.moderate',
    'content.view'
  ]
};

/**
 * Custom hook to access admin context
 */
export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

/**
 * Admin Provider Component
 */
export const AdminProvider = ({ children }) => {
  const { firebaseUser, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRole, setAdminRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [unreadAlertCount, setUnreadAlertCount] = useState(0);
  const [sessionValid, setSessionValid] = useState(false);
  const [sessionExpiry, setSessionExpiry] = useState(null);

  /**
   * Check if admin session is valid
   */
  const checkSession = useCallback(async () => {
    const sessionToken = sessionStorage.getItem('adminSession');
    const storedExpiry = sessionStorage.getItem('adminSessionExpiry');
    
    if (!sessionToken || !storedExpiry) {
      setSessionValid(false);
      return false;
    }
    
    // Check if session has expired locally
    if (new Date(storedExpiry) < new Date()) {
      sessionStorage.removeItem('adminSession');
      sessionStorage.removeItem('adminSessionExpiry');
      setSessionValid(false);
      return false;
    }
    
    // Validate with server and refresh
    try {
      const validateAdminSession = httpsCallable(functions, 'validateAdminSession');
      const result = await validateAdminSession({ sessionToken });
      
      if (result.data.valid) {
        sessionStorage.setItem('adminSessionExpiry', result.data.expiresAt);
        setSessionExpiry(new Date(result.data.expiresAt));
        setSessionValid(true);
        return true;
      }
    } catch (error) {
      console.error('Session validation error:', error);
      sessionStorage.removeItem('adminSession');
      sessionStorage.removeItem('adminSessionExpiry');
      setSessionValid(false);
    }
    
    return false;
  }, []);

  /**
   * End admin session (logout)
   */
  const endSession = useCallback(async () => {
    const sessionToken = sessionStorage.getItem('adminSession');
    
    if (sessionToken) {
      try {
        const endAdminSession = httpsCallable(functions, 'endAdminSession');
        await endAdminSession({ sessionToken });
      } catch (error) {
        console.error('Error ending session:', error);
      }
    }
    
    sessionStorage.removeItem('adminSession');
    sessionStorage.removeItem('adminSessionExpiry');
    setSessionValid(false);
    setSessionExpiry(null);
  }, []);

  /**
   * Check if user is an admin and get their role
   * Checks both adminUsers collection and user roles in users collection
   */
  useEffect(() => {
    const checkAdminStatus = async () => {
      console.log('[AdminContext] Checking admin status, firebaseUser:', firebaseUser?.uid, firebaseUser?.email);

      // During initial auth bootstrap, avoid resolving admin state as non-admin.
      // This prevents deep-link routes (e.g. /admin/email-templates) from
      // redirecting to home before Firebase auth rehydrates.
      if (authLoading) {
        setLoading(true);
        return;
      }
      
      if (!firebaseUser?.uid) {
        console.log('[AdminContext] No firebaseUser uid, setting isAdmin=false');
        setIsAdmin(false);
        setAdminRole(null);
        setPermissions([]);
        setSessionValid(false);
        setLoading(false);
        return;
      }

      try {
        // First, check the dedicated adminUsers collection by UID
        console.log('[AdminContext] Checking adminUsers collection by UID:', firebaseUser.uid);
        const adminDoc = await getDoc(doc(firestore, 'adminUsers', firebaseUser.uid));
        
        if (adminDoc.exists()) {
          const adminData = adminDoc.data();
          console.log('[AdminContext] Found admin by UID:', adminData);
          if (adminData.isActive !== false) {
            const normalizedRole = normalizeRole(adminData.role);
            // Handle custom permissions array or use role defaults
            const adminPerms = adminData.permissions?.includes('all') 
              ? ROLE_PERMISSIONS[ADMIN_ROLES.SUPER_ADMIN]
              : (ROLE_PERMISSIONS[normalizedRole] || []);
            
            setIsAdmin(true);
            setAdminRole(normalizedRole);
            setPermissions(adminPerms);
            
            // Check session validity
            await checkSession();
            setLoading(false);
            return;
          }
        }
        
        // Get user's email from multiple sources
        let userEmail = firebaseUser.email;
        console.log('[AdminContext] firebaseUser.email:', userEmail);
        
        // If no email on firebaseUser, try to get it from Firestore users collection
        if (!userEmail) {
          console.log('[AdminContext] No email on firebaseUser, checking users collection');
          const userDoc = await getDoc(doc(firestore, 'users', firebaseUser.uid));
          if (userDoc.exists()) {
            userEmail = userDoc.data().email;
            console.log('[AdminContext] Found email in users collection:', userEmail);
          }
        }
        
        // If found email, try looking up adminUsers by email as document ID
        if (userEmail) {
          const normalizedEmail = userEmail.toLowerCase();
          console.log('[AdminContext] Checking adminUsers with email as document ID:', normalizedEmail);
          const adminByEmailIdDoc = await getDoc(doc(firestore, 'adminUsers', normalizedEmail));
          
          if (adminByEmailIdDoc.exists()) {
            const adminData = adminByEmailIdDoc.data();
            console.log('[AdminContext] Found admin by email document ID:', adminData);
            if (adminData.isActive !== false) {
              const normalizedRole = normalizeRole(adminData.role);
              console.log('[AdminContext] Setting admin role:', normalizedRole);
              // Handle custom permissions array or use role defaults
              const adminPerms = adminData.permissions?.includes('all') 
                ? ROLE_PERMISSIONS[ADMIN_ROLES.SUPER_ADMIN]
                : (ROLE_PERMISSIONS[normalizedRole] || []);
              
              setIsAdmin(true);
              setAdminRole(normalizedRole);
              setPermissions(adminPerms);
              
              // Check session validity
              await checkSession();
              setLoading(false);
              return;
            }
          }
          
        }

        // Resolve any remaining admin identity SERVER-SIDE via a self-scoped
        // callable. This replaces a client-side `adminUsers` collection query
        // (previously: query adminUsers where email == mine), so the Firestore
        // `adminUsers` list rule can be locked to admins only — closing the
        // enumeration vector where any signed-in user could list every admin's
        // email/role. resolveMyAdmin resolves strictly from the caller's own
        // auth and also covers the users.role / .roles fallback below, so it is
        // authoritative for the common case.
        try {
          const resolveMyAdmin = httpsCallable(functions, 'resolveMyAdmin');
          const { data } = await resolveMyAdmin();
          if (data && data.isAdmin) {
            const normalizedRole = normalizeRole(data.role);
            const adminPerms = (Array.isArray(data.permissions) && data.permissions.includes('all'))
              ? ROLE_PERMISSIONS[ADMIN_ROLES.SUPER_ADMIN]
              : (ROLE_PERMISSIONS[normalizedRole] || []);
            setIsAdmin(true);
            setAdminRole(normalizedRole);
            setPermissions(adminPerms);
            await checkSession();
            setLoading(false);
            return;
          }
        } catch (resolveErr) {
          console.warn('[AdminContext] resolveMyAdmin failed; using users.role fallback:', resolveErr?.message);
        }

        // Fallback: users collection roles. Only reached if the callable
        // errored (resolveMyAdmin already covers this path server-side).
        const userDoc = await getDoc(doc(firestore, 'users', firebaseUser.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          // Gather every role spelling from both the array and legacy singular
          // field, lower-cased for matching.
          const roleStrings = [
            ...(Array.isArray(userData.roles) ? userData.roles : []),
            userData.role,
          ]
            .filter((r) => typeof r === 'string')
            .map((r) => r.trim().toLowerCase());

          // Only these strings grant admin access. IMPORTANT: do not blanket
          // normalizeRole() every role, because normalizeRole defaults unknown
          // values (e.g. 'member') to content_admin — which would wrongly
          // promote ordinary members. Match against the known admin-tier set.
          const ADMIN_TIER_ROLES = [
            'super_admin', 'superadmin', 'super_user', 'superuser',
            'content_admin', 'contentadmin', 'admin',
            'support_admin', 'supportadmin', 'support',
            'analytics_admin', 'analyticsadmin',
            'moderator',
          ];
          const matched = roleStrings.filter((r) => ADMIN_TIER_ROLES.includes(r));

          if (matched.length > 0) {
            // Pick the highest-privilege tier the user holds.
            const priority = {
              [ADMIN_ROLES.SUPER_ADMIN]: 5,
              [ADMIN_ROLES.CONTENT_ADMIN]: 4,
              [ADMIN_ROLES.SUPPORT_ADMIN]: 3,
              [ADMIN_ROLES.ANALYTICS_ADMIN]: 2,
              [ADMIN_ROLES.MODERATOR]: 1,
            };
            let mappedRole = ADMIN_ROLES.MODERATOR;
            let best = -1;
            matched.forEach((r) => {
              const norm = normalizeRole(r);
              if ((priority[norm] || 0) > best) {
                best = priority[norm] || 0;
                mappedRole = norm;
              }
            });

            setIsAdmin(true);
            setAdminRole(mappedRole);
            setPermissions(ROLE_PERMISSIONS[mappedRole] || []);

            // Check session validity
            await checkSession();
            setLoading(false);
            return;
          }
        }
        
        // User is not an admin
        setIsAdmin(false);
        setAdminRole(null);
        setPermissions([]);
        setSessionValid(false);
      } catch (error) {
        console.error('Error checking admin status:', error);
        setIsAdmin(false);
        setAdminRole(null);
        setPermissions([]);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
    // Only re-run when the Firebase user identity changes. `firebaseUser.email`
    // and the stable `checkSession` callback were previously listed but caused
    // the effect to re-fire repeatedly during the auth ramp-up; both are read
    // from the latest closure inside checkAdminStatus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseUser?.uid, authLoading]);

  /**
   * Session activity tracker - refresh on user activity
   */
  useEffect(() => {
    if (!isAdmin || !sessionValid) return;
    
    // Refresh session every 5 minutes of activity
    const intervalId = setInterval(() => {
      checkSession();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, [isAdmin, sessionValid, checkSession]);

  /**
   * Subscribe to system alerts
   */
  useEffect(() => {
    if (!isAdmin) return;

    // Only tiers with alerts.view may read systemAlerts (the Firestore rules
    // enforce this). Skip the subscription for lower tiers (e.g. moderator) so
    // we don't fire a denied read that logs a permission error and rejects.
    const canViewAlerts =
      adminRole === ADMIN_ROLES.SUPER_ADMIN ||
      permissions.includes('all') ||
      permissions.includes('alerts.view');
    if (!canViewAlerts) {
      setAlerts([]);
      setUnreadAlertCount(0);
      return;
    }

    const alertsQuery = query(
      collection(firestore, 'systemAlerts'),
      where('resolved', '==', false),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      alertsQuery,
      (snapshot) => {
        const alertsList = [];
        let unreadCount = 0;

        snapshot.forEach((doc) => {
          const alert = { id: doc.id, ...doc.data() };
          alertsList.push(alert);
          if (!alert.acknowledged && !alert.resolved) {
            unreadCount++;
          }
        });

        setAlerts(alertsList);
        setUnreadAlertCount(unreadCount);
      },
      (error) => {
        console.error('[AdminContext] Error subscribing to system alerts:', error);
        setAlerts([]);
        setUnreadAlertCount(0);
      }
    );

    return () => unsubscribe();
  }, [isAdmin, adminRole, permissions]);

  /**
   * Check if admin has a specific permission.
   * Super admins, and any role whose permission array contains the
   * wildcard 'all', are granted every permission.
   */
  const hasPermission = useCallback((permission) => {
    if (adminRole === ADMIN_ROLES.SUPER_ADMIN) return true;
    if (permissions.includes('all')) return true;
    return permissions.includes(permission);
  }, [permissions, adminRole]);

  /**
   * Check if admin has any of the specified permissions
   */
  const hasAnyPermission = useCallback((permissionList) => {
    if (adminRole === ADMIN_ROLES.SUPER_ADMIN) return true;
    if (permissions.includes('all')) return true;
    return permissionList.some(p => permissions.includes(p));
  }, [permissions, adminRole]);

  /**
   * Check if admin has all of the specified permissions
   */
  const hasAllPermissions = useCallback((permissionList) => {
    return permissionList.every(p => permissions.includes(p));
  }, [permissions]);

  const value = {
    isAdmin,
    adminRole,
    permissions,
    loading,
    alerts,
    unreadAlertCount,
    sessionValid,
    sessionExpiry,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    checkSession,
    endSession,
    ADMIN_ROLES
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;
